import { useCallback, useEffect, useState, type SyntheticEvent } from "react";
import Avatar from "@mui/material/Avatar";
import AvatarGroup from "@mui/material/AvatarGroup";
import Alert from "@mui/material/Alert";
import ButtonBase from "@mui/material/ButtonBase";
import Chip from "@mui/material/Chip";
import Fab from "@mui/material/Fab";
import Paper from "@mui/material/Paper";
import Skeleton from "@mui/material/Skeleton";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Tooltip from "@mui/material/Tooltip";
import { ArrowRight, CircleOff, Clock3, ExternalLink, Flame, Gamepad2, MessageCircle, Plus, ShieldCheck, Sparkles, Store } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import type { GameHallSummary, OfficialGroup, Paginated, Post, PostSort } from "../../shared/contracts";
import { PostCard } from "../components/PostCard";
import { StatePanel } from "../components/StatePanel";
import { Button } from "../components/ui/button";
import { useApp } from "../context/AppContext";
import { api } from "../lib/api";
import { openMallHome } from "../lib/external-links";

export function SquarePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedSort = searchParams.get("sort");
  const sort: PostSort = requestedSort === "popular" || requestedSort === "recommended" ? requestedSort : "latest";
  const requestedGameId = searchParams.get("game") ?? "";
  const requestedChannel = searchParams.get("channel") ?? "";
  const [posts, setPosts] = useState<Post[]>([]);
  const [groups, setGroups] = useState<OfficialGroup[]>([]);
  const [halls, setHalls] = useState<GameHallSummary[]>([]);
  const [hallsLoading, setHallsLoading] = useState(true);
  const [hallsError, setHallsError] = useState<string | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadMoreError, setLoadMoreError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { config, user, requestLogin } = useApp();
  const canPublish = Boolean(user && user.status !== "publish_restricted");
  const hero = config?.squareHero ?? {
    kicker: "号商免费发布账号实拍展示，看好素材直达商城下单。",
    title: "号商动态广场｜共享朋友圈",
    description: "欢迎来到动态广场！各大游戏号商在这里发布账号实拍截图、素材展示。这里只做内容种草，全部交易跳转商城完成，浏览喜欢的账号，一键前往商城选购。"
  };
  const selectedHall = halls.find((hall) => hall.id === requestedGameId) ?? halls[0] ?? null;
  const selectedChannel = requestedChannel === "account" || selectedHall?.channels.some((channel) => channel.id === requestedChannel)
    ? requestedChannel
    : "";
  const postQuery = new URLSearchParams({ limit: "10", sort });
  if (selectedHall) {
    postQuery.set("gameId", selectedHall.id);
    if (selectedChannel) postQuery.set("channel", selectedChannel);
  }

  useEffect(() => {
    let active = true;
    setHallsLoading(true);
    setHallsError(null);
    void api<{ items: GameHallSummary[] }>("/halls")
      .then((result) => { if (active) setHalls(result.items); })
      .catch((loadError) => { if (active) setHallsError(loadError instanceof Error ? loadError.message : "大厅加载失败"); })
      .finally(() => { if (active) setHallsLoading(false); });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    let active = true;
    void api<{ items: OfficialGroup[] }>("/groups")
      .then((result) => { if (active) setGroups(result.items.slice(0, 3)); });
    return () => { active = false; };
  }, [user?.id]);

  const load = useCallback(async () => {
    if (hallsLoading) return;
    setLoading(true);
    setError(null);
    try {
      if (!selectedHall) {
        setPosts([]);
        setCursor(null);
        return;
      }
      const postResult = await api<Paginated<Post>>(`/posts?${postQuery.toString()}`);
      setPosts(postResult.items);
      setCursor(postResult.nextCursor);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : "动态加载失败");
    } finally {
      setLoading(false);
    }
  }, [hallsLoading, postQuery.toString(), selectedHall]);

  useEffect(() => { void load(); }, [load, user?.id]);

  const loadMore = async () => {
    if (!cursor) return;
    setLoadingMore(true);
    setLoadMoreError(null);
    try {
      const query = new URLSearchParams(postQuery);
      query.set("cursor", cursor);
      const result = await api<Paginated<Post>>(`/posts?${query.toString()}`);
      setPosts((current) => [...current, ...result.items]);
      setCursor(result.nextCursor);
    } catch (loadError) {
      setLoadMoreError(loadError instanceof Error ? loadError.message : "更多动态加载失败");
    } finally {
      setLoadingMore(false);
    }
  };

  const changeSort = (nextSort: PostSort | null) => {
    if (!nextSort || nextSort === sort) return;
    setPosts([]);
    setCursor(null);
    setError(null);
    setLoadMoreError(null);
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.delete("view");
      if (selectedHall) next.set("game", selectedHall.id);
      if (nextSort === "latest") next.delete("sort");
      else next.set("sort", nextSort);
      return next;
    }, { replace: true });
  };

  const changeHall = (_event: SyntheticEvent, gameId: string) => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.delete("view");
      next.set("game", gameId);
      next.delete("channel");
      return next;
    }, { replace: true });
  };

  const changeChannel = (channelId: string) => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.delete("view");
      if (selectedHall) next.set("game", selectedHall.id);
      if (channelId) next.set("channel", channelId);
      else next.delete("channel");
      return next;
    }, { replace: true });
  };

  return (
    <div className="page-shell square-page">
      <section className="square-hero">
        <div className="hero-main">
          <div className="hero-copy t-stagger">
            <span className="hero-kicker t-stagger-line t-stagger-line--1"><Sparkles aria-hidden="true" />{hero.kicker}</span>
            <h1 className="t-stagger-line t-stagger-line--2">{hero.title}</h1>
            <p className="t-stagger-line t-stagger-line--3">{hero.description}</p>
          </div>
          <div className="hero-assurance" aria-label="平台使用说明">
            <span><ShieldCheck aria-hidden="true" /><span><strong>真实分享</strong><small>{config?.reviewRequired || config?.aiModerationEnabled ? "审核通过后公开" : "合规内容直接公开"}</small></span></span>
            <span><MessageCircle aria-hidden="true" /><span><strong>友好互动</strong><small>点赞、私信与社群交流</small></span></span>
          </div>
        </div>
        <Paper component="aside" variant="outlined" className="hero-community-card" aria-label="社区概况">
          <header><span><Sparkles aria-hidden="true" />社区现场</span><Chip size="small" color="success" variant="outlined" label="持续更新" /></header>
          <div className="community-stats">
            <span><strong>{hallsLoading ? "…" : selectedHall?.postCount ?? 0}</strong><small>当前大厅动态</small></span>
            <span><strong>{groups.length}</strong><small>官方社群</small></span>
          </div>
          <div className="community-avatars">
            <AvatarGroup max={4}>{groups.map((group) => <Avatar key={group.id} src={group.coverUrl ?? undefined}>{group.coverUrl ? null : group.gameName.slice(0, 1)}</Avatar>)}</AvatarGroup>
            <span><strong>和同游戏玩家聊聊</strong><small>找到你的游戏圈子</small></span>
          </div>
          <Button asChild variant="ghost" className="community-link"><Link to="/groups">查看全部社群<ArrowRight aria-hidden="true" /></Link></Button>
        </Paper>
      </section>

      <nav className="square-floating-actions" aria-label="首页快捷操作">
        {canPublish ? (
          <Tooltip title="发布动态" placement="left" enterDelay={400}>
            <Fab component={Link} to="/post/new" color="primary" className="square-floating-action" aria-label="发布动态"><Plus aria-hidden="true" /></Fab>
          </Tooltip>
        ) : user ? (
          <Tooltip title="发布权限已关闭" placement="left" enterDelay={400}>
            <span className="square-floating-action-anchor">
              <Fab color="primary" className="square-floating-action" type="button" disabled aria-label="发布权限已关闭"><CircleOff aria-hidden="true" /></Fab>
            </span>
          </Tooltip>
        ) : (
          <Tooltip title="登录后发布动态" placement="left" enterDelay={400}>
            <Fab color="primary" className="square-floating-action" type="button" aria-label="登录后发布动态" onClick={() => requestLogin("请先登录账号，才可以发布动态内容。", "/post/new")}><Plus aria-hidden="true" /></Fab>
          </Tooltip>
        )}
        <Tooltip title={config?.mallConfigured ? "去商城逛逛" : "商城未配置"} placement="left" enterDelay={400}>
          <span className="square-floating-action-anchor">
            <Fab className="square-floating-action square-floating-action-secondary" type="button" disabled={!config?.mallConfigured} aria-label={config?.mallConfigured ? "去商城逛逛" : "商城未配置"} onClick={() => config?.mallConfigured && openMallHome(config.mallBaseUrl)}><Store aria-hidden="true" /></Fab>
          </span>
        </Tooltip>
      </nav>

      <section className="hall-browser" aria-labelledby="hall-browser-title">
        <div className="hall-heading">
          <h2 id="hall-browser-title"><Gamepad2 aria-hidden="true" />游戏大厅</h2>
          {selectedHall ? <span className="hall-current"><strong>{selectedHall.name}</strong><small>{selectedHall.postCount} 条公开动态</small></span> : null}
        </div>
        <div className="hall-navigation">
          {hallsLoading ? <Skeleton className="hall-navigation-skeleton" variant="rounded" animation="wave" /> : null}
          {hallsError ? <Alert severity="error">{hallsError}</Alert> : null}
          {!hallsLoading && !hallsError && halls.length === 0 ? <StatePanel kind="empty" title="还没有可用的游戏大厅" description="管理员启用游戏后，大厅会自动显示在这里。" /> : null}
          {selectedHall ? <>
            <Tabs className="hall-game-tabs" value={selectedHall.id} onChange={changeHall} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile aria-label="选择游戏大厅">
              {halls.map((hall) => <Tab key={hall.id} value={hall.id} label={<span><strong>{hall.name}</strong></span>} />)}
            </Tabs>
            <div className="hall-channel-tabs" role="tablist" aria-label={`${selectedHall.name}大厅频道`}>
              <button type="button" role="tab" aria-selected={!selectedChannel} className={!selectedChannel ? "is-active" : ""} onClick={() => changeChannel("")}><span>全部</span></button>
              <button type="button" role="tab" aria-selected={selectedChannel === "account"} className={selectedChannel === "account" ? "is-active" : ""} onClick={() => changeChannel("account")}><span>游戏账号</span></button>
              {selectedHall.channels.map((channel) => <button type="button" role="tab" key={channel.id} aria-selected={selectedChannel === channel.id} className={selectedChannel === channel.id ? "is-active" : ""} onClick={() => changeChannel(channel.id)}><span>{channel.name}</span></button>)}
            </div>
          </> : null}
        </div>
      </section>

      <div className="square-content">
        <section className="feed-column" aria-label="朋友圈动态">
          <header className="feed-toolbar">
            <div className="feed-toolbar-actions"><ToggleButtonGroup className="feed-sort-control" size="small" value={sort} exclusive onChange={(_event, value: PostSort | null) => changeSort(value)} aria-label="动态排序方式"><ToggleButton value="latest" aria-label="按最新排序"><Clock3 aria-hidden="true" />最新</ToggleButton><ToggleButton value="popular" aria-label="按热门排序"><Flame aria-hidden="true" />热门</ToggleButton><ToggleButton value="recommended" aria-label="按推荐排序"><Sparkles aria-hidden="true" />推荐</ToggleButton></ToggleButtonGroup><Chip icon={<Gamepad2 aria-hidden="true" />} label={loading ? "正在加载" : `${posts.length} 条分享`} variant="outlined" /></div>
          </header>
          {loading ? <div className="post-skeletons" aria-label="正在加载动态" aria-live="polite"><Skeleton variant="rounded" animation="wave" /><Skeleton variant="rounded" animation="wave" /></div> : null}
          {error ? <StatePanel kind="error" description={error} onRetry={() => void load()} /> : null}
          {!loading && !error && posts.length === 0 ? <StatePanel kind="empty" title="这个频道还没有动态" description="新的公开动态会按发布者身份和玩家所选频道展示在这里。" /> : null}
          <div className="feed-list">
            {posts.map((post) => <PostCard key={post.id} initialPost={post} showFollow />)}
            {loadMoreError ? <StatePanel kind="error" title="更多动态加载失败" description={loadMoreError} onRetry={() => void loadMore()} /> : null}
            {cursor ? <Button className="load-more" variant="outline" type="button" disabled={loadingMore} onClick={() => void loadMore()}>{loadingMore ? "正在加载" : "加载更多动态"}</Button> : posts.length ? <p className="feed-end">已加载当前频道全部动态</p> : null}
          </div>
        </section>

        <aside className="discovery-rail" aria-label="广场辅助信息">
          <Paper component="section" variant="outlined" className="rail-card safety-card">
            <span className="rail-card-icon"><ShieldCheck aria-hidden="true" /></span>
            <span className="panel-kicker">社区守则</span>
            <h2>分享是主角，交流保持真诚</h2>
            <p>本广场只做内容展示和站内交流，禁止私下转账，全部交易请前往商城完成。</p>
            <Button className="button-full" variant="outline" type="button" disabled={!config?.mallConfigured} onClick={() => config?.mallConfigured && openMallHome(config.mallBaseUrl)}><Store aria-hidden="true" />去商城逛逛<ExternalLink aria-hidden="true" /></Button>
          </Paper>
          <Paper component="section" variant="outlined" className="rail-card community-card">
            <div className="panel-heading"><div><span className="panel-kicker">官方社群</span><h2>同游戏玩家</h2></div><Button asChild variant="ghost" size="icon"><Link to="/groups" aria-label="查看全部官方群"><ArrowRight aria-hidden="true" /></Link></Button></div>
            <div className="mini-group-list">
              {groups.map((group) => user
                ? <Link className="mini-group" key={group.id} to={`/groups/${group.id}`}><Avatar className="group-icon" src={group.coverUrl ?? undefined}>{group.coverUrl ? null : group.gameName.slice(0, 1)}</Avatar><span><strong>{group.name}</strong><small>{group.memberCount} 位成员</small></span><ArrowRight aria-hidden="true" /></Link>
                : <ButtonBase className="mini-group" key={group.id} type="button" onClick={() => requestLogin("请先登录账号，才可以进入官方群。", `/groups/${group.id}`)}><Avatar className="group-icon" src={group.coverUrl ?? undefined}>{group.coverUrl ? null : group.gameName.slice(0, 1)}</Avatar><span><strong>{group.name}</strong><small>{group.memberCount} 位成员</small></span><ArrowRight aria-hidden="true" /></ButtonBase>)}
            </div>
          </Paper>
          <Alert className="risk-strip" severity="error" icon={<ShieldCheck aria-hidden="true" />}><div><strong>社区内不做私下交易</strong><p>动态以分享和交流为主，涉及商品请通过已配置商城查看。</p></div></Alert>
        </aside>
      </div>
    </div>
  );
}
